import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { Link } from "@inertiajs/react";
import { Check, Lock, Minus, Lightbulb, ArrowUpRight, AlertTriangle, CalendarClock, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Notice } from "@/components/ui/notice";
import { formatDay, relativeDays } from "@/lib/campaignPlanning";
import { TIERS } from "@/components/schools/setup/steps";

// Small building blocks shared by the setup guide page, the dashboard
// setup card and the progress banner.

export function StepStatusIcon({ status, number, size = "md" }) {
  const box = size === "sm" ? "h-5 w-5 text-[10px]" : "h-7 w-7 text-xs";
  const icon = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";

  if (status === "done") {
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full bg-success text-success-foreground", box)}>
        <Check className={icon} strokeWidth={3} />
        <span className="sr-only">Done</span>
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/40 text-muted-foreground", box)}>
        <Minus className={icon} strokeWidth={3} />
        <span className="sr-only">Skipped</span>
      </span>
    );
  }
  if (status === "locked") {
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning-foreground", box)}>
        <Lock className={icon} strokeWidth={2.5} />
        <span className="sr-only">Locked until the campaign fee is paid</span>
      </span>
    );
  }
  if (status === "blocked") {
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full border border-border bg-secondary font-semibold text-muted-foreground/70 font-tabular", box)}>
        {number}
        <span className="sr-only">Not available yet</span>
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full border-2 border-primary bg-primary/10 font-semibold text-primary font-tabular", box)}>
        {number}
        <span className="sr-only">In progress</span>
      </span>
    );
  }
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center rounded-full border-2 border-border bg-card font-semibold text-muted-foreground font-tabular", box)}>
      {number}
    </span>
  );
}

export function TierBadge({ tier, className }) {
  const variant = { required: "default", recommended: "outline", optional: "secondary" }[tier];
  return (
    <Badge variant={variant} className={className} title={TIERS[tier].description}>
      {tier === "required" ? "Required to launch" : TIERS[tier].label}
    </Badge>
  );
}

// "Aim for Mon, Oct 5" — or "As soon as you can" once that's passed.
export function DueChip({ step, today, className }) {
  if (!step.due_on || step.status === "done" || step.status === "skipped") return null;

  if (step.overdue) {
    return (
      <span className={cn("inline-flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-0.5 text-xs font-medium text-warning-foreground", className)}>
        <AlertTriangle className="h-3 w-3" />
        As soon as you can
      </span>
    );
  }
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground", className)}>
      <CalendarClock className="h-3 w-3" />
      {step.key === "launch" ? "Goes live" : "Aim for"} {formatDay(step.due_on)}
      <span className="text-muted-foreground/70">· {relativeDays(today, step.due_on)}</span>
    </span>
  );
}

export function EffortChip({ effort }) {
  if (!effort) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Clock className="h-3 w-3" />
      {effort}
    </span>
  );
}

export function ProgressRing({ complete, total, size = 56, stroke = 5, label = true }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total ? complete / total : 0;

  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} className="stroke-border" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct)}
          className={cn("transition-[stroke-dashoffset] duration-700 motion-reduce:transition-none", pct === 1 ? "stroke-success" : "stroke-primary")}
        />
      </svg>
      {label && (
        <span className="absolute font-display font-semibold text-foreground font-tabular" style={{ fontSize: size / 4 }}>
          {complete}/{total}
        </span>
      )}
      <span className="sr-only">{complete} of {total} steps done</span>
    </span>
  );
}

// Advice drawn from PagePledge's planning guide and help centre.
export function GuideTip({ title, children, href, linkLabel }) {
  return (
    <div className="rounded-xl border border-accent/20 bg-accent/5 p-4">
      <div className="flex gap-3">
        <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
        <div className="min-w-0 space-y-1.5 text-sm">
          {title && <p className="font-medium text-foreground">{title}</p>}
          <div className="leading-relaxed text-muted-foreground [&_p+p]:mt-2">{children}</div>
          {href && (
            <a href={href} target="_blank" rel="noopener" className="inline-flex items-center gap-1 font-medium text-accent hover:underline">
              {linkLabel}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// What to know before doing a step. It always goes above the step's
// fields and buttons; GuideTips (optional extra advice) go at the end.
export function StepIntro({ children, className }) {
  return <div className={cn("max-w-3xl space-y-2 text-sm leading-relaxed text-muted-foreground", className)}>{children}</div>;
}

// The setup guide's name for the shared Notice.
export function StepNotice({ tone = "info", children, action }) {
  return <Notice tone={tone} action={action}>{children}</Notice>;
}

// One button for every kind of step action: an Inertia link, a plain
// link (downloads, Square's consent screen), or copying text to the
// clipboard.
export function ActionButton({ action, primary = false, size = "sm" }) {
  const [copied, copyText] = useCopyToClipboard();
  const variant = primary ? "default" : "outline";
  const Icon = copied ? Check : action.icon;
  const content = (
    <>
      {Icon && <Icon className="h-4 w-4" />}
      {copied ? "Copied" : action.label}
    </>
  );

  if (action.copyText) {
    return <Button type="button" size={size} variant={variant} onClick={() => copyText(action.copyText)}>{content}</Button>;
  }

  return (
    <Button size={size} variant={variant} asChild>
      {action.external
        ? <a href={action.href}>{content}</a>
        : <Link href={action.href}>{content}</Link>}
    </Button>
  );
}

export function ActionRow({ actions }) {
  const buttons = actions.filter(Boolean);
  if (buttons.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {buttons.map((action, i) => (
        <ActionButton key={action.label} action={action} primary={action.primary ?? i === 0} />
      ))}
    </div>
  );
}
